import React from 'react';
import { useIntl } from 'react-intl';
import type { TooltipProps } from '@mui/material/Tooltip';
import Chip, { type ChipProps } from '@mui/material/Chip';
import { styled } from '@mui/material/styles';

import Tooltip from '@/components/mui/Tooltip';

// Figma (node 21297-28298): 24px tall, warning.main on contrast text, Rubik 13/18 regular, radius 8.
// A real Chip carries all of that from the design system — `size="small"` gives the 24px box and the
// theme's MuiChip radius, `color="warning"` the palette pair. Only two things still need pinning:
// the small size drops the font to 12px, and Chip sets no font-weight, so the badge would inherit
// the host row's (bold inside the nav item) — the exact drift DA-19422 flagged.
const BetaChip = styled(Chip, { label: 'BetaChip' })(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  fontWeight: theme.typography.fontWeightRegular,
}));

export interface BetaBadgeProps extends ChipProps {
  /**
   * Forwarded to the underlying Tooltip's `slotProps`. Used by the chat header to
   * lift the tooltip Popper above the chat Drawer (zIndex 1600) so it isn't hidden.
   */
  tooltipSlotProps?: TooltipProps['slotProps'];
}

const BetaBadge: React.FC<BetaBadgeProps> = ({ tooltipSlotProps, ...props }) => {
  const { formatMessage } = useIntl();

  return (
    <Tooltip
      title={formatMessage({
        id: 'tooltip.betaBadge',
        defaultMessage: 'Beta feature: Currently under evaluation and customers feedback gathering',
      })}
      slotProps={tooltipSlotProps}
    >
      <BetaChip
        size="small"
        color="warning"
        label={formatMessage({ id: 'labels.beta', defaultMessage: 'Beta' })}
        {...props}
      />
    </Tooltip>
  );
};

export default BetaBadge;
